import React from 'react';

export type MarketStatus = 'open' | 'pre-market' | 'after-hours' | 'closed';

// ── Fallback: local calculation (used only when the backend API is unavailable) ──
// Checks weekday + ET trading hours only. Holidays are not handled here —
// Finnhub is the authoritative source for those.

export function getMarketStatusFallback(): MarketStatus {
  const now = new Date();

  const etParts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hour12: false,
  }).formatToParts(now);

  const get = (type: string) => etParts.find((p) => p.type === type)?.value ?? '';

  const hour    = parseInt(get('hour'), 10);
  const minute  = parseInt(get('minute'), 10);
  const weekday = get('weekday');

  const timeInMinutes = hour * 60 + minute;

  const OPEN        = 9  * 60 + 30;
  const CLOSE       = 16 * 60;
  const PRE_OPEN    = 4  * 60;
  const AFTER_CLOSE = 20 * 60;

  if (weekday === 'Sat' || weekday === 'Sun') return 'closed';
  if (timeInMinutes >= OPEN  && timeInMinutes < CLOSE)        return 'open';
  if (timeInMinutes >= PRE_OPEN && timeInMinutes < OPEN)      return 'pre-market';
  if (timeInMinutes >= CLOSE && timeInMinutes < AFTER_CLOSE)  return 'after-hours';
  return 'closed';
}

// ── API fetch ──

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

async function fetchMarketStatus(): Promise<MarketStatus> {
  const res = await fetch(`${API_URL}/market-status`, { signal: AbortSignal.timeout(5_000) });
  if (!res.ok) throw new Error(`market-status: ${res.status}`);
  const data = await res.json();
  return data.status as MarketStatus;
}

// ── React hook ──

/**
 * Returns the current US market status.
 * Primary source: backend /api/market-status (Finnhub).
 * Fallback:       local time-based calculation.
 * Refreshes every 60 seconds.
 */
export function useMarketStatus(): MarketStatus {
  const [status, setStatus] = React.useState<MarketStatus>(getMarketStatusFallback);

  const refresh = React.useCallback(async () => {
    try {
      const s = await fetchMarketStatus();
      setStatus(s);
    } catch {
      setStatus(getMarketStatusFallback());
    }
  }, []);

  React.useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  return status;
}
